import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../apiClient";

import CoverSection from "./sections/CoverSection";

// Six income editors (basic & advanced for each bucket)
import IncomeSection from "./sections/IncomeSection";                    // basic — income
import IncomeAdvancedSection from "./sections/IncomeAdvancedSection";    // advanced — income
import IncomeAssessorSection from "./sections/IncomeAssessorSection";    // basic — assessor
import IncomeAssessorAdvancedSection from "./sections/IncomeAssessorAdvancedSection"; // advanced — assessor
import IncomeActualsSection from "./sections/IncomeActualsSection";      // basic — actuals
import IncomeActualsAdvancedSection from "./sections/IncomeActualsAdvancedSection";   // advanced — actuals

import EqualUniformSection from "./sections/EqualUniformSection";
import EqualUniformIncomeSection from "./sections/EqualUniformIncomeSection";
import SalesSection from "./sections/SalesSection";
import CostSection from "./sections/CostSection";
import CostAssessorSection from "./sections/CostAssessorSection";
import AttachmentsSection from "./sections/AttachmentsSection";
import PrintSection from "./sections/PrintSection";


// NEW
import HistorySection from "./sections/HistorySection";
import InternalNotesSection from "./sections/InternalNotesSection";

// Popup modal windows
import IncomeApproachComparisonPopupWindow from "./modals/IncomeApproachComparisonPopupWindow";

import { CompareHoverProvider, CompareHoverPopover } from "./modals/CompareHoverContext";


/**
 * PacketBuilder
 * - Seeds draft ONCE per packet id (no re-clobbering after saves).
 * - Shallow-merges section objects so an editor can’t wipe sibling fields.
 * - Keeps six income sections fully isolated (basic/advanced for each bucket).
 */
export default function PacketBuilder({ packet: initialPacket, onClose }) {
  const packetId = initialPacket?.id ?? initialPacket?.packet_id ?? null;

  const alreadyHydrated =
    !!initialPacket &&
    Array.isArray(initialPacket.sections) &&
    typeof initialPacket.sections.length === "number";

  const [packet, setPacket] = useState(alreadyHydrated ? initialPacket : null);
  const [loading, setLoading] = useState(!alreadyHydrated && !!packetId);
  const [error, setError] = useState("");

  // ---------- Section catalog ----------
  const SECTION_META = useMemo(
    () => [
      { type: "cover",                    label: "Cover Page",                    icon: IconCover,       editor: CoverSection },

      // Income group — six discrete sections (labels normalized to "basic ..." / "advanced ...")
      { type: "income",                   label: "Basic Income",                  icon: IconIncome,      editor: IncomeSection },
      { type: "income_advanced",          label: "Advanced Income",               icon: IconIncome,      editor: IncomeAdvancedSection },

      { type: "income_assessor",          label: "Basic Income (Assessor)",       icon: IconIncome,      editor: IncomeAssessorSection },
      { type: "income_assessor_advanced", label: "Advanced Income (Assessor)",    icon: IconIncome,      editor: IncomeAssessorAdvancedSection },

      { type: "income_actuals",           label: "Basic Income (Actuals)",        icon: IconIncome,      editor: IncomeActualsSection },
      { type: "income_actuals_advanced",  label: "Advanced Income (Actuals)",     icon: IconIncome,      editor: IncomeActualsAdvancedSection },

      { type: "eu",                       label: "Equal & Uniform",               icon: IconEU,          editor: EqualUniformSection },
      { type: "eu_income",                label: "Equal & Uniform (Income)",      icon: IconEU,          editor: EqualUniformIncomeSection }, // NEW

      { type: "sales",                    label: "Sales Approach",                icon: IconSales,       editor: SalesSection },
      { type: "cost",                     label: "Cost Approach",                 icon: IconCost,        editor: CostSection },
      { type: "cost_assessor",            label: "CAD Cost Approach (Beta)",      icon: IconCost,        editor: CostAssessorSection },
      { type: "attachments",              label: "Attachments",                   icon: IconAttachments, editor: AttachmentsSection },

      // NEW: History section (editor)
      { type: "history",                  label: "History",                       icon: IconHistory,     editor: HistorySection },
	  
	  // NEW: Internal Notes / Tools (never printed)
      { type: "internal_notes",           label: "Internal Notes / Tools",        icon: IconNotes,       editor: InternalNotesSection },

      // Print planner
      { type: "print",                    label: "Print / PDF",                   icon: IconPrint,       editor: PrintSection },
    ],
    []
  );
  const TYPES = useMemo(() => SECTION_META.map((s) => s.type), [SECTION_META]);

  // ---------- Hydrate from API ----------
  const hydrate = useCallback(async () => {
    if (!packetId || alreadyHydrated) return;
    setError("");
    setLoading(true);
    let cancelled = false;
    try {
      const res = await api.get(`/bapb/packets/${packetId}`);
      if (!cancelled) setPacket(res?.data || null);
    } catch (e) {
      if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Failed to load packet");
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [packetId, alreadyHydrated]);

  useEffect(() => {
    if (alreadyHydrated) {
      setPacket(initialPacket);
      setLoading(false);
      setError("");
      return;
    }
    if (!packetId) {
      setPacket(null);
      setLoading(false);
      setError("");
      return;
    }
    hydrate();
  }, [packetId, alreadyHydrated, initialPacket, hydrate]);

  // ---------- Sorted + latest per type ----------
  const sortedSections = useMemo(() => {
    return (packet?.sections || [])
      .slice()
      .sort((a, b) => (a?.order_index ?? 0) - (b?.order_index ?? 0));
  }, [packet]);

  const latestByType = useMemo(() => {
    const m = Object.create(null);
    for (const s of sortedSections) {
      const t = String(s.type || "").toLowerCase();
      const curr = m[t];
      if (!curr || (s.order_index ?? 0) >= (curr.order_index ?? 0)) m[t] = s;
    }
    return m;
  }, [sortedSections]);

  // ---------- Normalizers ----------
  function normalizeCoverPropertyInfo(piRaw = {}) {
    const out = {
      account: piRaw.account ?? piRaw.parcel ?? "",
      site_name: piRaw.site_name ?? "",
      address: piRaw.address ?? "",
      city: piRaw.city ?? "",
      state: piRaw.state ?? "TX",
      zip: piRaw.zip ?? "",
      client_name: piRaw.client_name ?? piRaw.company ?? "",
      owner_name: piRaw.owner_name ?? piRaw.owner ?? "",
      client_address: piRaw.client_address ?? "",
      assessor: piRaw.assessor ?? "",
      property_type: piRaw.property_type ?? "",
      year_built: piRaw.year_built ?? "",
      unit_type: piRaw.unit_type ?? "units",
      units: piRaw.units ?? piRaw.sqft ?? "",
      land_sf: piRaw.land_sf ?? piRaw.land_area_sf ?? "",
      occupancy: piRaw.occupancy ?? "",
      client_site_code: piRaw.client_site_code ?? piRaw.company_code ?? "",
      cad_location_code: piRaw.cad_location_code ?? piRaw.location_code ?? "",
      // Value mode + breakdown fields
      value_mode: piRaw.value_mode ?? "total",
      py_land: piRaw.py_land ?? "",
      py_building: piRaw.py_building ?? "",
      previous_year_value: piRaw.previous_year_value ?? piRaw.py_appraised_value ?? "",
      cy_land: piRaw.cy_land ?? "",
      cy_building: piRaw.cy_building ?? "",
      current_year_value: piRaw.current_year_value ?? piRaw.appraised_value ?? "",
      opinion_of_value: piRaw.opinion_of_value ?? "",
      opinion_source: piRaw.opinion_source || "custom",
      consultant_name: piRaw.consultant_name ?? "",
      consultant_phone: piRaw.consultant_phone ?? "",
      consultant_email: piRaw.consultant_email ?? "",
      notes: piRaw.notes ?? piRaw.internal_notes ?? "",
    };
    out.parcel = out.account;
    return out;
  }

  function normalizeIncome(inRaw = {}, coverPI = {}) {
    const li = inRaw?.line_items || {};
    return {
      ...inRaw,
      potential_gross: inRaw.potential_gross ?? inRaw.rent ?? "",
      expenses:        inRaw.expenses ?? inRaw.opex ?? "",
      cap_rate:        inRaw.cap_rate ?? inRaw.base_cap_rate ?? "",
      line_items: {
        income:   Array.isArray(li.income)   ? li.income   : [],
        vcloss:   Array.isArray(li.vcloss)   ? li.vcloss   : (Array.isArray(li.vacancy) ? li.vacancy : []),
        expenses: Array.isArray(li.expenses) ? li.expenses : [],
      },
      area_sf: inRaw.area_sf ?? coverPI?.units ?? "",
      notes: inRaw.notes || "",
    };
  }
  const normalizeIncomeAdvanced = normalizeIncome; // advanced uses same shape (explicit line_items buckets)

  // ---------- Draft (autosave) ----------
  const storageKey = packetId ? `bapb:packet:${packetId}:draft` : null;

  const defaults = useMemo(() => {
    const d = Object.create(null);

    const piServer = latestByType.cover?.data?.property_info ?? {};
    const pi = normalizeCoverPropertyInfo(piServer);

    // Basic Income
    const inc = normalizeIncome(latestByType.income?.data?.income ?? {}, pi);

    // Advanced Income (seed from itself if present, else from basic)
    const incAdvSeed = latestByType.income_advanced?.data?.income_advanced ?? {};
    const incAdv = normalizeIncomeAdvanced(incAdvSeed, pi);

    // Assessor
    const incAssessor = normalizeIncome(latestByType.income_assessor?.data?.income_assessor ?? {}, pi);
    const incAssessorAdvSeed =
      latestByType.income_assessor_advanced?.data?.income_assessor_advanced ??
      (latestByType.income_assessor?.data?.income_assessor ?? {});
    const incAssessorAdv = normalizeIncomeAdvanced(incAssessorAdvSeed, pi);

    // Actuals
    const incActuals = normalizeIncome(latestByType.income_actuals?.data?.income_actuals ?? {}, pi);
    const incActualsAdvSeed =
      latestByType.income_actuals_advanced?.data?.income_actuals_advanced ??
      (latestByType.income_actuals?.data?.income_actuals ?? {});
    const incActualsAdv = normalizeIncomeAdvanced(incActualsAdvSeed, pi);

    const coverImages   = latestByType.cover?.data?.images   || {};
    const coverBranding = latestByType.cover?.data?.branding || {};

    d.cover                       = { property_info: pi, images: coverImages, branding: coverBranding,};
    d.income                      = { income: inc };
    d.income_advanced             = { income_advanced: incAdv };
    d.income_assessor             = { income_assessor: incAssessor };
    d.income_assessor_advanced    = { income_assessor_advanced: incAssessorAdv };
    d.income_actuals              = { income_actuals: incActuals };
    d.income_actuals_advanced     = { income_actuals_advanced: incActualsAdv };

    // Equal & Uniform — Income
    const euIncFromServer = latestByType.eu_income?.data;
    d.eu_income = (euIncFromServer && typeof euIncFromServer === "object")
      ? (euIncFromServer.equal_uniform_income ? euIncFromServer : { equal_uniform_income: euIncFromServer })
      : { equal_uniform_income: { rows: [], config: { columns: {}, baseline: {} } } };

    // Equal & Uniform (preserve whatever shape server returns; ensure nested under equal_uniform)
    const euFromServer = latestByType.eu?.data;
    d.eu = (euFromServer && typeof euFromServer === "object")
      ? (euFromServer.equal_uniform ? euFromServer : { equal_uniform: euFromServer })
      : { equal_uniform: { rows: [], config: {} } };

    d.sales = { sales: (latestByType.sales?.data?.sales) ?? { comps: [] } };
    d.cost  = { cost: (latestByType.cost?.data?.cost) ?? {
      replacement_cost: "",
      depreciation_pct: "",
      depreciation: "",
      land_value: "",
      soft_cost_pct: "",
      notes: "",
    } };

    // CAD Cost Approach (Beta) — preserve whatever the server returned.
    const costAssessorFromServer = latestByType.cost_assessor?.data;
    d.cost_assessor = (costAssessorFromServer && typeof costAssessorFromServer === "object")
      ? (costAssessorFromServer.cost_assessor ? costAssessorFromServer : { cost_assessor: costAssessorFromServer })
      : { cost_assessor: {} };
    d.attachments = {
      attachments: Array.isArray(latestByType.attachments?.data?.attachments)
        ? latestByType.attachments.data.attachments
        : [],
    };

    // NEW: History defaults (if server has one, preserve it)
    const histFromServer = latestByType.history?.data;
    d.history = (histFromServer && typeof histFromServer === "object")
      ? (histFromServer.history ? histFromServer : { history: histFromServer })
      : { history: {
          rows: [{ year: new Date().getFullYear(), value:"", income:"", expenses:"", vacancy_pct:"" }],
          print: { value:true, income:true, expenses:false, vacancy:false, noi:true }
        } };
		
		
	// NEW: Internal notes (purely internal, never printed)
    const internalNotesFromServer = latestByType.internal_notes?.data;
    d.internal_notes =
      internalNotesFromServer && typeof internalNotesFromServer === "object"
        ? (internalNotesFromServer.internal_notes
            ? internalNotesFromServer
            : { internal_notes: internalNotesFromServer })
        : {
            internal_notes: {
              notes: "",
              calc: { a: "", b: "" },
              cap: { noi: "", capRate: "", value: "" },
            },
          };	

    // Print (seed empty; the Print editor will show smart defaults)
    d.print = latestByType.print?.data ?? { print: {} };

    return d;
  }, [latestByType]);

  const [draft, setDraft] = useState(defaults);
  const [dirty, setDirty] = useState({});

  // ✅ Seed ONCE per packet id
  const hydratedIdRef = useRef(null);
  useEffect(() => {
    const idKey = packetId || "__noid__";
    if (hydratedIdRef.current !== idKey) {
      setDraft(defaults);
      setDirty({});
      hydratedIdRef.current = idKey;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packetId]);

  // Load saved local draft (merges over initial seed)
  useEffect(() => {
    if (!storageKey) return;
    // require a known server timestamp before deciding
    const serverUpdated = packet?.updated_at ? new Date(packet.updated_at).getTime() : 0;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const draftTs = Number(parsed?.ts || 0);
      // Only hydrate if local draft is actually newer than server
      if (draftTs > serverUpdated && parsed?.draft && typeof parsed.draft === "object") {
        setDraft(prev => ({ ...prev, ...parsed.draft }));
        setDirty(parsed.dirty || {});
      } else {
        // Server is same/newer → discard stale draft so it never masks fresh data
        localStorage.removeItem(storageKey);
      }
    } catch {
      /* ignore */
    }
  }, [storageKey, packet?.updated_at]);

  // Persist to local storage
  useEffect(() => {
    if (!storageKey) return;
    const payload = JSON.stringify({ draft, dirty, ts: Date.now() });
    localStorage.setItem(storageKey, payload);
  }, [storageKey, draft, dirty]);

  // ---------- UI state ----------
  const [activeType, setActiveType] = useState(() => TYPES.find((t) => !!latestByType[t]) || "cover");

  // Popup window state
  const [incomeCompareOpen, setIncomeCompareOpen] = useState(false);

  useEffect(() => {
    if (!latestByType[activeType]) {
      const fallback = TYPES.find((t) => !!latestByType[t]) || "cover";
      setActiveType(fallback);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestByType]);

  // Normalizes the payload shape the server expects for each section.
  // Must live INSIDE the PacketBuilder component so it can share scope.
  function normalizeSectionDataForType(type, obj) {
    const safe = obj && typeof obj === "object" ? obj : {};

    switch (type) {
      case "income":                   return safe.income                   ? safe : { income: (safe.income ?? safe) };
      case "income_advanced":          return safe.income_advanced          ? safe : { income_advanced: (safe.income_advanced ?? safe) };
      case "income_assessor":
        if (safe.income_assessor)      return safe;
        if (safe.income)               return { income_assessor: safe.income };
        return { income_assessor: {} };

      case "income_assessor_advanced":
        if (safe.income_assessor_advanced) return safe;
        if (safe.income_advanced)          return { income_assessor_advanced: safe.income_advanced };
        return { income_assessor_advanced: {} };

      case "income_actuals":
        if (safe.income_actuals)       return safe;
        if (safe.income)               return { income_actuals: safe.income };
        return { income_actuals: {} };

      case "income_actuals_advanced":
        if (safe.income_actuals_advanced) return safe;
        if (safe.income_advanced)         return { income_actuals_advanced: safe.income_advanced };
        return { income_actuals_advanced: {} };

      // NEW: History saves under { history: {...} }
      case "history":
        return safe.history ? safe : { history: (safe.history ?? safe) };
		
	  // NEW: Internal notes saved under { internal_notes: {...} }
      case "internal_notes":
        return safe.internal_notes ? safe : { internal_notes: (safe.internal_notes ?? safe) };

      // CAD Cost Approach (Beta) — envelope: { cost_assessor: {...} }
      case "cost_assessor":
        return safe.cost_assessor ? safe : { cost_assessor: (safe.cost_assessor ?? safe) };

      default:                         return safe; // EU, Sales, Cost, Attachments, Print...
    }
  }

  // ---------- Helpers & server calls ----------
  const nextOrderIndex = useMemo(() => {
    const max = (packet?.sections || []).reduce((m, s) => Math.max(m, s?.order_index ?? 0), 0);
    return max + 1;
  }, [packet]);

  const markDirty = (type, value = true) => setDirty((prev) => ({ ...prev, [type]: !!value }));

  // Shallow-merge section root so editors can’t wipe sibling fields
  const setDraftForType = (type, newData) => {
    setDraft((prev) => {
      const prevTypeObj = prev?.[type] || {};
      const nextTypeObj = newData || {};
      return { ...prev, [type]: { ...prevTypeObj, ...nextTypeObj } };
    });
    markDirty(type, true);
  };

  // Save one section; allows an override payload from an editor to avoid races.
  const saveSection = async (type, dataOverride) => {
    if (!packetId) return;

    // Pull the current draft (or editor override) and NORMALIZE it
    // into the exact, non-null envelope the API expects.
    const raw = dataOverride ?? draft[type];
    const data = normalizeSectionDataForType(type, raw);

    try {
      const res = await api.post(`/bapb/packets/${packetId}/sections`, {
        type,
        order_index: nextOrderIndex,
        data,
      });

      setPacket(res.data);        // update server snapshot (green dot)
      markDirty(type, false);     // clear dirty flag

      // If we saved with an override, keep local draft in sync
      if (dataOverride) {
        setDraft((prev) => ({ ...prev, [type]: { ...(prev?.[type] || {}), ...dataOverride } }));
      }
    } catch (e) {
      alert(e?.response?.data?.detail || `Failed to save ${type}`);
    }
  };

  const saveAll = async () => {
    // Take a snapshot so async saves don't fight over the live `dirty` object.
    const dirtySnapshot = { ...dirty };

    for (const t of TYPES) {
      if (dirtySnapshot[t]) {
        // eslint-disable-next-line no-await-in-loop
        await saveSection(t);
      }
    }

    // After all saves complete, force-clear all dirty flags so every section,
    // including History, shows as "Saved" until the next edit.
    setDirty({});
  };

  const patchPacket = async (patch) => {
    if (!packetId) return;
    try {
      const res = await api.patch(`/bapb/packets/${packetId}`, patch);
      setPacket(res.data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to update packet");
    }
  };

  const downloadJSON = () => {
    const exportObj = {
      id: packet?.id,
      account_number: packet?.account_number,
      tax_year: packet?.tax_year,
      title: packet?.title,
      status: packet?.status,
      sections: TYPES.map((t, i) => ({
        type: t,
        order_index: i,
        data: draft[t],
      })),
    };
    const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `packet_${packet?.account_number}_${packet?.tax_year}.json`;
    a.click();
  };

  // ------- Delete packet -------
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDeletePacket = async () => {
    if (deleteConfirmText !== "DELETE") return;
    setDeleting(true);
    try {
      await api.delete(`/bapb/packets/${packetId}`);
      onClose?.();
    } catch (e) {
      alert("Failed to delete packet: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setDeleteConfirmText("");
    }
  };

  const STATUS_OPTIONS = [
    "Draft",
    "Started",
    "Ready to Send",
    "Sent",
    "Toplined",
    "Settled",
    "Hearing",
  ];

  // ------- Tax Year inline editor -------
  const [editYear, setEditYear] = useState(packet?.tax_year || "");
  useEffect(() => {
    setEditYear(packet?.tax_year || "");
  }, [packet?.tax_year]);

  const commitYear = async () => {
    const raw = String(editYear).trim();
    if (!raw) return;
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1900 || n > 2100) {
      alert("Please enter a valid year between 1900 and 2100.");
      setEditYear(packet?.tax_year || "");
      return;
    }
    if (n === packet?.tax_year) return; // nothing to do
    await patchPacket({ tax_year: n });
  };

  // --- "Save" button behavior (save year + all dirty sections) ---
  const hasPendingYear = String(editYear ?? "") !== String(packet?.tax_year ?? "");
  const anyDirty = hasPendingYear || Object.values(dirty).some(Boolean);

  const saveEverything = async () => {
    try {
      if (hasPendingYear) {
        await commitYear();
      }
      await saveAll();
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    }
  };

  // ---------- Indicated value resolver for dropdown ----------
  const num2 = (v) => {
    if (v == null || v === "") return 0;
    const s = String(v).replace(/[$,%\s,]/g, "");
    const x = Number(s);
    return Number.isFinite(x) ? x : 0;
  };

  // UPDATED: prefer adjusted/final values when present; otherwise fall back to indicated
  const extractAdjustedFirst = (typeObj) => {
    if (!typeObj || typeof typeObj !== "object") return 0;

    const ADJUSTED_KEYS = new Set([
      "final_value",
      "adjusted_value",
      "adjusted_total_value",
      "adj_total_value",
      "adjusted_total",
      "value_after_deduction",
      "net_value",
      "net_total_value",
    ]);

    const INDICATED_KEYS = new Set([
      "indicated_value",
      "indicated_total_value",
      "indicated_total",
      "concluded_value",
      "conclusion",
      "value",
      "total_value",
      "subject_indicated_value",
    ]);

    const seen = new Set();
    let adjusted = undefined;
    let indicated = undefined;

    const num2 = (v) => {
      if (v == null || v === "") return 0;
      const s = String(v).replace(/[$,%\s,]/g, "");
      const n = Number(s);
      return Number.isFinite(n) ? n : 0;
    };

    const walk = (node) => {
      if (!node || typeof node !== "object" || seen.has(node)) return;
      seen.add(node);

      if (Array.isArray(node)) {
        for (const it of node) walk(it);
        return;
      }

      for (const [k, v] of Object.entries(node)) {
        const key = (k || "").toLowerCase();
        if (ADJUSTED_KEYS.has(key)) {
          const n = num2(v);
          if (n > 0) adjusted = Math.max(adjusted ?? 0, n);
        } else if (INDICATED_KEYS.has(key)) {
          const n = num2(v);
          if (n > 0) indicated = Math.max(indicated ?? 0, n);
        }
        if (v && typeof v === "object") walk(v);
      }
    };

    walk(typeObj);
    return adjusted ?? indicated ?? 0;
  };

  const valueOptions = useMemo(() => {
    // Build an option per SECTION_META (excluding cover)
    return SECTION_META
      .filter(s => s.type !== "cover")
      .map(({ type, label }) => {
        const dataForType = draft?.[type] || {};
        return { type, label, value: extractAdjustedFirst(dataForType) };
      });
  }, [draft, SECTION_META]);

  // ---------- Render ----------
  if (loading) return <PreparingBuilder />;
  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
        <div className="font-medium text-red-800 mb-2">Failed to load packet</div>
        <div className="text-red-700">{String(error)}</div>
      </div>
    );
  }

  const ActiveEditor =
    SECTION_META.find((s) => s.type === activeType)?.editor || NullEditor;

  const headerTitle =
    (packet?.account_number ? `${packet.account_number}` : "Packet") +
    (packet?.tax_year ? ` — ${packet.tax_year}` : "");

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3">
        <div className="text-lg font-semibold text-slate-800 mr-auto">
          {headerTitle}
          {packet?.status ? (
            <span className="ml-3 text-sm text-slate-500">Status: {packet.status}</span>
          ) : null}
        </div>

        {/* ▼ Tax Year editor */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">Year</label>
          <input
            type="number"
            inputMode="numeric"
            min={1900}
            max={2100}
            className="year-input"
            value={editYear}
            onChange={(e) => setEditYear(e.target.value)}
            onBlur={commitYear}
            onKeyDown={(e) => e.key === "Enter" && commitYear()}
            placeholder="YYYY"
          />
        </div>

        {/* ▼ Status dropdown */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">Status</label>
          <select
            className="status-select"
            value={packet?.status || "Draft"}
            onChange={(e) => patchPacket({ status: e.target.value })}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {/* One Save button */}
        <button
          className="btn-primary"
          onClick={saveEverything}
          disabled={!anyDirty}
          title={anyDirty ? "Save year & all unsaved sections" : "Nothing to save"}
        >
          Save
        </button>

        <button className="btn" onClick={downloadJSON}>Download JSON</button>

        <button
          className="btn text-red-500 hover:bg-red-50 hover:text-red-700 border-red-200"
          onClick={() => setShowDeleteModal(true)}
          title="Permanently delete this packet"
        >
          Delete
        </button>
      </div>

      {/* ── Delete confirmation modal ── */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-red-50 px-6 py-4 border-b border-red-100">
              <h3 className="text-lg font-bold text-red-700">Delete Packet</h3>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-slate-700">
                This will <span className="font-semibold">permanently delete</span> this packet,
                all its sections, and all uploaded attachments. This action cannot be undone.
              </p>
              <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                <span className="font-semibold text-slate-800">{packet?.account_number}</span>
                {packet?.title ? ` — ${packet.title}` : ""}
                {packet?.tax_year ? ` (${packet.tax_year})` : ""}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm
                </label>
                <input
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && deleteConfirmText === "DELETE" && handleDeletePacket()}
                  placeholder="DELETE"
                  autoFocus
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
              <button
                className="btn text-sm"
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(""); }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                disabled={deleteConfirmText !== "DELETE" || deleting}
                onClick={handleDeletePacket}
              >
                {deleting ? "Deleting..." : "Delete Permanently"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main grid (wider rail on lg) */}
      <div className="builder-grid grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Sections rail */}
        <aside className="lg:col-span-1">
          <div className="rounded-2xl border border-slate-200 bg-white p-3">
            <div className="text-slate-600 text-sm mb-2">Sections</div>
            <ul className="space-y-2">
              {SECTION_META.map(({ type, label, icon: Icon }) => {
                const exists = !!latestByType[type];
                const active = activeType === type;
                return (
                  <li key={type}>
                    <button
                      type="button"
                      onClick={() => setActiveType(type)}
                      className={[
                        "flex w-full items-center gap-2 rounded-lg border px-2 py-2",
                        active ? "border-sky-300 bg-sky-50 text-sky-900" : "border-slate-200 bg-white text-slate-800 hover:border-slate-300",
                      ].join(" ")}
                    >
                      <Icon />
                      <span className="truncate whitespace-nowrap">{label}</span>
                      <span className="ml-auto inline-flex items-center">
                        <span
                          className={[
                            "inline-block h-2.5 w-2.5 rounded-full",
                            exists ? "bg-emerald-500" : "bg-slate-300",
                          ].join(" ")}
                          title={exists ? "Saved" : "Not saved yet"}
                        />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
			{/* Modals */}
            <div className="mt-4 pt-3 border-t border-slate-100">
              <div className="text-slate-600 text-sm mb-2">Modals</div>
              <ul className="space-y-2">
                <li>
                  <button
                    type="button"
                    onClick={() => setIncomeCompareOpen(true)}
                    className="flex w-full items-center gap-2 rounded-lg border px-2 py-2 border-slate-200 bg-white text-slate-800 hover:border-slate-300"
                  >
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700">
                      ⇄
                    </span>
                    <span className="truncate whitespace-nowrap">Income Approach Comparison</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </aside>

        {/* Active editor */}
        <section className="lg:col-span-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-base font-semibold text-slate-800">
                {labelForType(activeType)}
              </div>
              <div className="text-xs text-slate-500">
                {dirty[activeType] ? <span className="text-amber-600">Unsaved</span> : existsText(latestByType[activeType])}
              </div>
            </div>

            <CompareHoverProvider packet={packet} latestByType={latestByType} draft={draft}>
			  <ActiveEditor
				packet={packet}
				data={draft[activeType]}
				onChange={(next) => setDraftForType(activeType, next)}
				onSave={(override) => saveSection(activeType, override)}
				// pass section value options into Cover & Print
				{...((activeType === "cover" || activeType === "print") ? { valueOptions } : {})}
				// pass packetId explicitly to Attachments editor
				{...(activeType === "attachments" ? { packetId } : {})}
			  />

			  <CompareHoverPopover />
			</CompareHoverProvider>
          </div>
        </section>
      </div>
	  
	  {/* Popup window */}
      <IncomeApproachComparisonPopupWindow
        open={incomeCompareOpen}
        onClose={() => setIncomeCompareOpen(false)}
        packetId={packetId}
        latestByType={latestByType}
        draft={draft}
      />
	  
      {/* Local styles */}
      <style>{`
        .btn { border:1px solid #e5e7eb; background:#fff; padding:6px 10px; border-radius:10px; font-size:14px; }
        .btn:hover { border-color:#d1d5db; }
        .btn-primary { border:1px solid #93c5fd; background:#eff6ff; color:#1d4ed8; padding:6px 10px; border-radius:10px; font-size:14px; }
        .btn-primary:hover { border-color:#60a5fa; }
        .status-select, .year-input {
          border: 1px solid #e5e7eb;
          background: #fff;
          border-radius: 10px;
          padding: 6px 10px;
          font-size: 14px;
        }
        .status-select:hover, .year-input:hover { border-color:#d1d5db; }
        .year-input { width: 96px; }
        @media (min-width: 1024px) {
          .builder-grid { grid-template-columns: 320px 1fr 1fr 1fr; }
        }
        @media (min-width: 1280px) {
          .builder-grid { grid-template-columns: 340px 1fr 1fr 1fr; }
        }
        @media (min-width: 1536px) {
          .builder-grid { grid-template-columns: 360px 1fr 1fr 1fr; }
        }
        @media (min-width: 1920px) {
          .builder-grid { grid-template-columns: 380px 1fr 1fr 1fr; }
        }
      `}</style>
    </div>
  );
}

/* --------------------------------- helpers --------------------------------- */

function PreparingBuilder() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="text-sm text-slate-600 mb-3">Preparing builder…</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-20 rounded-xl bg-slate-100 animate-pulse" />
        ))}
      </div>
    </div>
  );
}
function NullEditor() {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
      No editor available for this section.
    </div>
  );
}

function labelForType(t) {
  switch (t) {
    case "cover": return "Cover Page";
    case "income": return "Basic Income";
    case "income_advanced": return "Advanced Income";
    case "income_assessor": return "Basic Income (Assessor)";
    case "income_assessor_advanced": return "Advanced Income (Assessor)";
    case "income_actuals": return "Basic Income (Actuals)";
    case "income_actuals_advanced": return "Advanced Income (Actuals)";
    case "eu": return "Equal & Uniform";
    case "eu_income": return "Equal & Uniform (Income)";
    case "sales": return "Sales Approach";
    case "cost": return "Cost Approach";
    case "attachments": return "Attachments";
    case "history": return "History"; // NEW
	case "internal_notes": return "Internal Notes / Tools";
    case "print": return "Print / PDF";
    default: return t || "Section";
  }
}
function existsText(s) {
  if (!s) return "Not saved yet";
  const dt = s?.updated_at || s?.created_at;
  return dt ? `Saved ${new Date(dt).toLocaleString()}` : "Saved";
}

// Tiny inline icons
function IconCover() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-slate-600">
      <rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 7h8M8 11h8M8 15h8"/>
    </svg>
  );
}
function IconIncome() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-slate-600">
      <path d="M3 3v18h18"/><path d="M7 14l4-4 3 3 5-5"/>
    </svg>
  );
}
function IconEU() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-slate-600">
      <path d="M12 3v18M3 7h18M6 7l3 5H3l3-5Zm9 0l3 5h-6l3-5Z" />
    </svg>
  );
}
function IconSales() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-slate-600">
      <path d="M4 6h16M4 12h16M4 18h16"/><circle cx="8" cy="6" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="16" cy="18" r="1"/>
    </svg>
  );
}
function IconCost() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-slate-600">
      <circle cx="12" cy="12" r="9"/><path d="M12 7v10M8 9h6a3 3 0 0 1 0 6H8"/>
    </svg>
  );
}
function IconAttachments() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-slate-600">
      <path d="M21 15V7a4 4 0 0 0-8 0v10a6 6 0 1 1-12 0V7" />
    </svg>
  );
}
function IconPrint() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-slate-600">
      <path d="M6 9V3h12v6M6 14H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1M6 14h12v7H6v-7Z" />
    </svg>
  );
}
function IconHistory() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-slate-600">
      <path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 3v6h6"/><path d="M12 7v6l4 2"/>
    </svg>
  );
}

function IconNotes() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      className="text-slate-600"
    >
      <rect x="4" y="3" width="14" height="18" rx="2" />
      <path d="M8 8h6M8 12h4M8 16h3" />
    </svg>
  );
}